import { AccountInfo, TokenData } from '@/types'
import { jwtDecode } from 'jwt-decode'
import { User, UserManager } from 'oidc-client-ts'
import { defineStore } from 'pinia'

import { oidcSettings } from '../oidcConfig'
import { usePermissionStore } from '@/stores/permission-store'
import { getAccountInfo } from '@/api'
import { useGlobalStore } from './global-store'
import { useConsentStore } from './consent-store'
import { useAnnouncementStore } from './announcement-store'

export const useAuthStore = defineStore({
  id: 'auth',

  state: () => ({
    token: null as string | null,
    refreshToken: null as string | null,
    returnUrl: '',
    userManager: new UserManager(oidcSettings),
    user: null as User | null,
    tokenData: null as TokenData | null,
    accountInfo: null as AccountInfo | null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.user,
    getName: (state) => state.tokenData?.name || '',
    getUserName: (state) => state.tokenData?.preferred_username || '',
    getEmail: (state) => state.tokenData?.email || '',
    getCoachId: (state) => state.tokenData?.coach_id || 0,
    getPatientId: (state) => state.tokenData?.patient_id || 0,
    getUserId: (state) => state.tokenData?.sub || 0,
    getAccountId: (state) => state.accountInfo?.organization.id || 0,
    getAccountOrganizationType: (state) => state.accountInfo?.organization.type,
    getAccountOrganizationState: (state) => state.accountInfo?.organization.state,
    isInactive: (state) => state.accountInfo?.isInactive,
  },

  actions: {
    async login() {
      if (window.location.pathname !== '/callback') {
        this.returnUrl = window.location.pathname + window.location.search + window.location.hash
      }
      await this.userManager.signinRedirect({ state: { returnUrl: this.returnUrl } })
    },

    async handleRedirectCallback() {
      try {
        const user = await this.userManager.signinRedirectCallback()
        if (!user) return this.clearUserData()

        this.setUser(user)

        const state = user.state as { returnUrl?: string } | undefined
        if (state?.returnUrl) {
          this.returnUrl = state.returnUrl
        }
      } catch (error) {
        console.error('Authorization error:', error)
      }
    },

    async logout() {
      const permissionStore = usePermissionStore()
      const globalStore = useGlobalStore()
      const consentStore = useConsentStore()
      const announcementStore = useAnnouncementStore()

      try {
        await this.userManager.removeUser()
        this.clearUserData()
        this.clearAccountInfo()
        permissionStore.permissions = []
        globalStore.clearGlobalStore()
        consentStore.clear()
        announcementStore.clear()
        localStorage.removeItem('language')
        localStorage.removeItem('dismissedAnnouncements')

        await this.userManager.signoutRedirect()
      } catch (error) {
        console.error('Signout error:', error)
      }
    },

    async checkUserAuthenticated() {
      const user = await this.userManager.getUser()
      if (user) {
        this.setUser(user)
        await useConsentStore().loadRequiredConsents()
      }
    },

    async refreshAccessToken() {
      try {
        const user = await this.userManager.signinSilent()
        if (!user) throw new Error('Failed to refresh token')

        this.setUser(user)
        await useConsentStore().loadRequiredConsents()
      } catch (error) {
        console.error('Refresh token error:', error)
        await this.logout()
      }
    },

    async loadAccountInfo() {
      const rawInfo = await getAccountInfo()
      this.accountInfo = new AccountInfo(rawInfo)
    },

    clearUserData() {
      this.user = null
      this.token = null
      this.refreshToken = null
      this.tokenData = null
    },

    clearAccountInfo() {
      this.accountInfo = null
    },

    setUser(user: User) {
      this.user = user
      this.token = user.access_token
      this.refreshToken = user.refresh_token || null
      this.tokenData = jwtDecode<TokenData>(user.access_token)
    },
  },
})
